import jwtDecode from 'jwt-decode';
import axios from 'axios';
import { useSelector, useDispatch } from 'react-redux';
import cookieService from './cookieService';
import {
  ID_TOKEN_COOKIE,
  ACCESS_TOKEN_COOKIE,
  providerListEndpoint,
  ACCESS_ROLES,
} from '../config';
import userDetails from '../actions/userDetailsAction';
import getHeaders from '../utils/getHeaders';
import { logger } from '../logger';

const log = logger();

const decodeIdToken = (idToken) => {
  let decoded = {};
  try {
    decoded = jwtDecode(idToken);
  } catch (err) {
    log.error('invalid token', err);
    return {};
  }
  const firstName = decoded.given_name;
  const lastName = decoded.family_name;
  const role = decoded['https://www.cochlear.com/role'].at(-1);
  const professionalId =
    decoded['https://www.cochlear.com/cochlear_id'];
  const countryCode =
    decoded['https://www.cochlear.com/country_code'];
  const username = decoded.email;
  const languageCode = decoded['https://www.cochlear.com/language'];
  // TO BE CONFIRMED
  const dateOfBirth = decoded['https://www.cochlear.com/dob'] || '';
  const partyId = decoded['https://www.cochlear.com/party_id'] || '';
  const region = decoded['https://www.cochlear.com/region'] || '';
  const loginAppName =
    decoded['https://www.cochlear.com/login_app_name'] || 'dpxportal';

  return {
    firstName,
    lastName,
    role,
    professionalId,
    countryCode,
    username,
    dateOfBirth,
    partyId,
    region,
    languageCode,
    loginAppName,
  };
};

const decodeAccessToken = (accessToken) => {
  let decoded = {};
  try {
    decoded = jwtDecode(accessToken);
  } catch (err) {
    log.error('invalid access token', err);
    return {};
  }

  const act = decoded['https://www.cochlear.com/act'];
  const actRole = act && act.role ? act.role : [];
  const actProviderId = act && act.sub ? act.sub : '';

  const roles = Array.isArray(actRole) ? actRole : [];

  // For now, specifically look for Patient Management role.
  // May need to rework this once we have additional roles in the future
  const hasPMRole = roles.some(
    (userRole) =>
      userRole.toLowerCase() ===
      ACCESS_ROLES.PATIENT_MANAGEMENT.toLowerCase(),
  );
  let providerRole = null;
  if (hasPMRole) {
    providerRole = ACCESS_ROLES.PATIENT_MANAGEMENT;
  }

  return {
    providerRole,
    actProviderId,
  };
};

const getUserDetails = () => {
  const dispatch = useDispatch();

  // look for user details in global state
  const uDetails = useSelector((state) => state.userDetails);
  if (uDetails) return uDetails;

  // if not found, read the id cookie
  const idToken = cookieService.getCookie(ID_TOKEN_COOKIE);
  // read access token cookie
  const accessToken = cookieService.getCookie(ACCESS_TOKEN_COOKIE);

  let decodedUserDetails = {};
  let updatedUserDetails = {};

  if (idToken) {
    decodedUserDetails = decodeIdToken(idToken);
    updatedUserDetails = { ...decodedUserDetails };
  }

  // retrieve role from access token if available
  if (accessToken) {
    const { providerRole } = decodeAccessToken(accessToken);

    if (providerRole) {
      updatedUserDetails = {
        ...decodedUserDetails,
        role: providerRole,
      };
    }
  }

  // store the user info in state
  dispatch(userDetails(updatedUserDetails));

  return updatedUserDetails;
};

const getCountryConfig = async (country) => {
  try {
    const response = await axios.get(
      `${providerListEndpoint}/country-config/${country}`,
      getHeaders(),
    );
    return response.data;
  } catch (err) {
    log.error('Error getting country config', err);
    return null;
  }
};

const getAccessTokenClaims = () => {
  const accessToken = cookieService.getCookie(ACCESS_TOKEN_COOKIE);

  if (accessToken) {
    return decodeAccessToken(accessToken);
  }

  return {};
};

const userService = {
  decodeIdToken,
  decodeAccessToken,
  getUserDetails,
  getAccessTokenClaims,
  getCountryConfig,
};

export default userService;
